import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { SecondaryButton } from '../../components/button/SecondaryButton';
import { DatePicker } from '../../components/date-picker/DatePicker';
import { StatCard } from '../../components/StatCard';
import { useFetchTrips } from '../../hooks/useFetchTrips';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { t, tPlural } from '../../services/translation/translation';
import { formatDate } from '../../utils/date';

export const DailyStats = () => {
  const [date, setDate] = useState(Date.now());
  const [isVisible, setIsVisible] = useState(false);

  const ref = useOnClickOutside(
    useCallback(() => {
      setIsVisible(false);
    }, []),
  );

  const handleChange = useCallback((timestamp: number | undefined) => {
    setDate(timestamp ?? Date.now());
    setIsVisible(false);
  }, []);

  const dayInterval = { start: dayjs(date).startOf('day').valueOf(), end: dayjs(date).endOf('day').valueOf() };

  const doneTripsQuery = useFetchTrips(
    { state: ['DONE'], limit: 1, departureAt: dayInterval },
    { keepPreviousData: true },
  );
  const doneTripsTotal = doneTripsQuery.data?.pages[0].totalItems ?? 0;

  const incomingTripsQuery = useFetchTrips(
    { state: ['INCOMING', 'ONDUTY', 'ONPAYMENT'], limit: 1, departureAt: dayInterval },
    { keepPreviousData: true },
  );
  const incomingTripsTotal = incomingTripsQuery.data?.pages[0].totalItems ?? 0;

  const bookedTripsQuery = useFetchTrips({ limit: 1, bookedAt: dayInterval }, { keepPreviousData: true });
  const bookedTripsTotal = bookedTripsQuery.data?.pages[0].totalItems ?? 0;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <SecondaryButton text={formatDate(date, t('date.date-longer'))} onClick={() => setIsVisible(true)} />
        {isVisible ? (
          <div
            ref={ref}
            className={twMerge(
              'absolute top-16 z-10 w-48 bg-white rounded-lg shadow dark:bg-gray-700',
              !isVisible && 'hidden',
            )}
          >
            <DatePicker onChange={handleChange} initialDate={date} />
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-center gap-4 mb-4">
        <StatCard
          text={`${doneTripsTotal}`}
          caption={tPlural('stats.nb-trips-done', { count: doneTripsTotal })}
          isFetching={doneTripsQuery.isLoading || (doneTripsQuery.isPreviousData && doneTripsQuery.isFetching)}
        />
        <StatCard
          text={`${incomingTripsTotal}`}
          caption={tPlural('stats.nb-trips-incoming', { count: incomingTripsTotal })}
          isFetching={
            incomingTripsQuery.isLoading || (incomingTripsQuery.isPreviousData && incomingTripsQuery.isFetching)
          }
        />
        <StatCard
          text={`${bookedTripsTotal}`}
          caption={tPlural('stats.nb-trips-booked', { count: bookedTripsTotal })}
          isFetching={bookedTripsQuery.isLoading || (bookedTripsQuery.isPreviousData && bookedTripsQuery.isFetching)}
        />
      </div>
    </div>
  );
};
